/**
 * Session + localStorage cache for confirmed CRM writes.
 *
 * The details panel remounts on every task switch. Without a sync cache it
 * paints "Write to Attio" until `/crm-link` returns, then snaps to
 * "View in Attio". Matching is keyed by the CRM proposal fingerprint so
 * "Try again" (a new proposal) still shows Write.
 */

const memoryCache = new Map();

const crmWrittenProposalStorageKey = (itemKey) =>
  `starmesh:attio-written-proposal:${String(itemKey || "").trim()}`;

/**
 * Normalize a cached CRM write into a stable shape.
 *
 * @example
 * normalizeCrmWrittenLink({ proposalKey: '{"object_type":"people"}', recordId: "rec_1" });
 * // { proposalKey: '{"object_type":"people"}', recordId: "rec_1", webUrl: "", writtenAt: "", wasCreated: null }
 *
 * @param {object} value
 * @returns {{ proposalKey: string, recordId: string, webUrl: string, writtenAt: string, wasCreated: boolean | null } | null}
 */
const normalizeCrmWrittenLink = (value) => {
  if (!value || typeof value !== "object") return null;
  const proposalKey = String(value.proposalKey || "").trim();
  if (!proposalKey) return null;
  return {
    proposalKey,
    recordId: String(value.recordId || "").trim(),
    webUrl: String(value.webUrl || "").trim(),
    writtenAt: String(value.writtenAt || "").trim(),
    wasCreated: typeof value.wasCreated === "boolean" ? value.wasCreated : null,
  };
};

/**
 * Parse localStorage: new `{ proposalKey, recordId, webUrl }` JSON, or the
 * legacy raw proposal-key string.
 *
 * @example
 * parseStoredCrmWrittenValue('{"proposalKey":"{\\"object_type\\":\\"people\\"}","recordId":"rec_1"}');
 * // { proposalKey: '{"object_type":"people"}', recordId: "rec_1", webUrl: "" }
 *
 * @example
 * parseStoredCrmWrittenValue('{"object_type":"people"}');
 * // { proposalKey: '{"object_type":"people"}', recordId: "", webUrl: "" }
 *
 * @param {string} raw
 * @returns {{ proposalKey: string, recordId: string, webUrl: string } | null}
 */
export const parseStoredCrmWrittenValue = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object" && parsed.proposalKey) {
      return normalizeCrmWrittenLink(parsed);
    }
  } catch {
    // Fall through: legacy values are the proposal JSON itself (also starts with `{`).
  }
  return { proposalKey: text, recordId: "", webUrl: "", writtenAt: "", wasCreated: null };
};

const readStoredCrmWrittenLink = (itemKey) => {
  const key = String(itemKey || "").trim();
  if (!key || typeof localStorage === "undefined") return null;
  try {
    return parseStoredCrmWrittenValue(localStorage.getItem(crmWrittenProposalStorageKey(key)));
  } catch {
    return null;
  }
};

/**
 * Read the cached CRM write for a task (memory, then localStorage).
 *
 * @example
 * peekCrmWrittenLink("wf::key::file");
 * // { proposalKey: '{"object_type":"people"}', recordId: "rec_1", webUrl: "https://..." }
 *
 * @param {string} itemKey
 * @returns {{ proposalKey: string, recordId: string, webUrl: string } | null}
 */
export const peekCrmWrittenLink = (itemKey) => {
  const key = String(itemKey || "").trim();
  if (!key) return null;
  if (memoryCache.has(key)) return memoryCache.get(key);
  const stored = readStoredCrmWrittenLink(key);
  if (stored) memoryCache.set(key, stored);
  return stored;
};

/**
 * Remember a confirmed CRM write so remounts / reloads can paint View immediately.
 *
 * @example
 * rememberCrmWrittenLink("wf::key::file", {
 *   proposalKey: '{"object_type":"people"}',
 *   recordId: "rec_1",
 *   webUrl: "https://app.attio.com/people/rec_1",
 *   writtenAt: "2026-09-03T15:30:00Z",
 *   wasCreated: true,
 * });
 *
 * @param {string} itemKey
 * @param {{ proposalKey?: string, recordId?: string, webUrl?: string, writtenAt?: string, wasCreated?: boolean }} link
 */
export const rememberCrmWrittenLink = (itemKey, link = {}) => {
  const key = String(itemKey || "").trim();
  const existing = peekCrmWrittenLink(key) || {};
  const next = normalizeCrmWrittenLink({
    proposalKey: link.proposalKey || existing.proposalKey,
    recordId: link.recordId || existing.recordId,
    webUrl: link.webUrl || existing.webUrl,
    writtenAt: link.writtenAt || existing.writtenAt,
    wasCreated: typeof link.wasCreated === "boolean" ? link.wasCreated : existing.wasCreated,
  });
  if (!key || !next) return;
  memoryCache.set(key, next);
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(crmWrittenProposalStorageKey(key), JSON.stringify(next));
  } catch {
    // Ignore quota / private-mode failures.
  }
};

/**
 * Resolve Write vs View state synchronously from the performer result or cache.
 *
 * A matching cached proposal is treated as already written even before `/crm-link`
 * returns, so the UI never flashes "Write to Attio" for a confirmed write.
 *
 * @example
 * resolveCrmWrittenUiState({
 *   result: {},
 *   itemKey: "wf::key::file",
 *   proposalKey: '{"object_type":"people"}',
 * });
 * // { recordId: "rec_1", webUrl: "https://app.attio.com/...", writtenAt: null, wasCreated: null } when cache matches
 *
 * @param {{ result?: object, itemKey?: string, proposalKey?: string }} args
 * @returns {{ recordId: string | null, webUrl: string | null, writtenAt: string | null, wasCreated: boolean | null }}
 */
export const resolveCrmWrittenUiState = ({ result, itemKey, proposalKey } = {}) => {
  const fromResultId = result?.crm_written_record_id || null;
  const fromResultUrl = result?.crm_written_web_url || null;
  if (fromResultId) {
    const cached = peekCrmWrittenLink(itemKey);
    const wasCreatedFromResult =
      typeof result?.crm_was_created === "boolean" ? result.crm_was_created : null;
    return {
      recordId: fromResultId,
      webUrl: fromResultUrl || cached?.webUrl || null,
      writtenAt: result?.crm_written_at || cached?.writtenAt || null,
      wasCreated:
        wasCreatedFromResult !== null
          ? wasCreatedFromResult
          : typeof cached?.wasCreated === "boolean"
            ? cached.wasCreated
            : null,
    };
  }
  const cached = peekCrmWrittenLink(itemKey);
  const expectedProposalKey = String(proposalKey || "").trim();
  if (cached && cached.proposalKey && cached.proposalKey === expectedProposalKey) {
    return {
      recordId: cached.recordId || "written",
      webUrl: cached.webUrl || null,
      writtenAt: cached.writtenAt || null,
      wasCreated: typeof cached.wasCreated === "boolean" ? cached.wasCreated : null,
    };
  }
  return { recordId: null, webUrl: null, writtenAt: null, wasCreated: null };
};

/**
 * Overlay cached CRM write fields onto a performer result for first paint.
 *
 * @example
 * applyCachedCrmWrittenFields({ type: "crm", client_item_id: "wf::k::f", crm_proposed_payload: {} }, '{"object_type":""}');
 * // adds crm_written_record_id / crm_written_web_url / crm_written_at / crm_was_created when the cache matches
 *
 * @param {object | null} result
 * @param {string} proposalKey
 * @returns {object | null}
 */
export const applyCachedCrmWrittenFields = (result, proposalKey) => {
  if (!result || result.type !== "crm") return result;
  const itemKey = String(result.client_item_id || result.action_item || "").trim();
  const resolved = resolveCrmWrittenUiState({ result, itemKey, proposalKey });
  if (!resolved.recordId) return result;
  if (
    result.crm_written_record_id === resolved.recordId &&
    (result.crm_written_web_url || null) === (resolved.webUrl || null) &&
    (result.crm_written_at || null) === (resolved.writtenAt || null) &&
    (result.crm_was_created ?? null) === (resolved.wasCreated ?? null)
  ) {
    return result;
  }
  return {
    ...result,
    crm_written_record_id: resolved.recordId,
    crm_written_web_url: resolved.webUrl || result.crm_written_web_url || null,
    crm_written_at: resolved.writtenAt || result.crm_written_at || null,
    crm_was_created:
      typeof resolved.wasCreated === "boolean" ? resolved.wasCreated : result.crm_was_created ?? null,
  };
};

/**
 * Clear in-memory cache (tests only). localStorage keys are left to the test.
 *
 * @example
 * resetCrmWrittenLinkCacheForTests();
 */
export const resetCrmWrittenLinkCacheForTests = () => {
  memoryCache.clear();
};
